// Utilitário compartilhado pra avatar do usuário.
//
// O avatar fica em R2 (path `users/{uid}/avatar.jpg`), servido pela Pages
// Function `/api/users/avatar?uid=...&v=...`. O parâmetro `v` é o timestamp
// salvo em `users/{uid}.avatarVersion` no Firestore — assim o cache do
// navegador invalida automaticamente quando o user troca a foto.
//
// Quando não há foto, a UI renderiza as iniciais do nome num círculo com
// gradient warm (mesmo padrão do design system).

import { auth } from '../../core/firebase.js';

export const HAS_AVATAR_KEY = 'avatarVersion';

// No app (Capacitor) a origem do WebView é `localhost`, então uma URL relativa
// `/api/...` num <img>/background-image bateria no próprio WebView e daria 404.
// O patch de fetch do nativeApiBasePlugin NÃO cobre carregamentos de imagem,
// então montamos a URL absoluta aqui usando a base exposta pelo plugin. Na web
// a base é vazia → URL relativa, comportamento idêntico ao de antes.
function apiUrl(path) {
  const base = (typeof window !== 'undefined' && window.__MAPIARE_API_BASE__) || '';
  return base + path;
}

/**
 * Constrói a URL pra o avatar de um user.
 * Retorna null se não houver foto (avatarVersion === null/undefined/0).
 */
export function avatarUrl(uid, settings) {
  if (!uid) return null;
  const version = settings?.[HAS_AVATAR_KEY];
  if (!version) return null;
  return apiUrl(`/api/users/avatar?uid=${encodeURIComponent(uid)}&v=${version}`);
}

/**
 * URL do avatar do usuário logado atualmente. Atalho.
 */
export function selfAvatarUrl(settings) {
  const uid = auth.currentUser?.uid;
  return avatarUrl(uid, settings);
}

/**
 * Iniciais a partir de um nome ("Ana Martins" → "AM"; "Gabriel" → "GA";
 * "ana@x.com" → "AN"). Sempre 1-2 chars maiúsculos. Default 'M' (Mapiare).
 */
export function avatarInitials(name) {
  const s = String(name || '').trim();
  if (!s) return 'M';
  const parts = s.split(/\s+|@/).filter(Boolean);
  if (!parts.length) return 'M';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Renderiza HTML de avatar — `<span class="avatar-photo">` com a foto
 * como background-image se houver, ou texto puro com as iniciais.
 *
 * Por que background-image em vez de `<img>`:
 *   `<img>` é replaced element com intrinsic size (a foto vem 512×512). Em
 *   `<button>` com flex layout, mesmo com width:100%/height:100%, alguns
 *   browsers respeitam o intrinsic size primeiro e o overflow:hidden corta
 *   uma região mínima → "zoom extremo". Background-image sempre respeita
 *   `background-size: cover` corretamente.
 *
 * Fallback de erro: como background-image não dispara `onerror`, fazemos
 * preflight JS via `hydrateAvatarPhotos` (chamada após cada render que
 * pode trocar avatares). Se a URL retorna 404, o JS troca o span pelas
 * iniciais.
 *
 * O caller é responsável pelo container `.avatar` (background-color de
 * fallback, tamanho, font, flex-centering).
 */
export function renderAvatarHtml({ uid, settings, name }) {
  const url = avatarUrl(uid, settings);
  const initials = escHtml(avatarInitials(name));
  if (url) {
    return `<span class="avatar-photo" data-photo-url="${escHtml(url)}" data-fallback="${initials}" role="img" aria-label="Foto de perfil"></span>`;
  }
  return initials;
}

/**
 * Como `renderAvatarHtml`, mas pra OUTROS usuários — não temos
 * `settings.avatarVersion` deles (rules permitem read em users/{uid}
 * só pra self). Constrói URL sem version; o cache HTTP de 1h pode
 * deixar a foto stale por até 1h após uma troca, mas isso é OK pra
 * avatares de membros em listas. Se a pessoa não tem foto, o GET
 * retorna 404 e o onerror cai pras iniciais via hydrateAvatarPhotos.
 */
export function renderOtherAvatarHtml({ uid, name }) {
  const initials = escHtml(avatarInitials(name));
  if (!uid) return initials;
  const url = apiUrl(`/api/users/avatar?uid=${encodeURIComponent(uid)}`);
  return `<span class="avatar-photo" data-photo-url="${escHtml(url)}" data-fallback="${initials}" role="img" aria-label="Foto de perfil"></span>`;
}

/**
 * Faz preflight de carregamento das fotos renderizadas em `root`. Pra cada
 * `.avatar-photo` com `data-photo-url`, baixa a imagem em paralelo:
 *   - success → seta `background-image` no span
 *   - error   → substitui o span pelas iniciais (via `data-fallback`)
 *
 * Idempotente: ignora spans que já foram hidratados (sem `data-photo-url`).
 */
export function hydrateAvatarPhotos(root = document) {
  const spans = root.querySelectorAll('.avatar-photo[data-photo-url]');
  for (const span of spans) {
    const url = span.dataset.photoUrl;
    if (!url) continue;
    span.removeAttribute('data-photo-url');   // marca como em-andamento
    const img = new Image();
    img.onload = () => {
      span.style.backgroundImage = `url(${JSON.stringify(url)})`;
      span.classList.add('is-loaded');
    };
    img.onerror = () => {
      const fb = span.dataset.fallback || 'M';
      // Decode HTML entities (escHtml encoded as &amp; etc); usa textNode
      const txt = document.createElement('textarea');
      txt.innerHTML = fb;
      span.replaceWith(document.createTextNode(txt.value));
    };
    img.src = url;
  }
}

function escHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
